import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';

export const CompanySelector = ({
  apiBaseUrl,
  domainValue,
  onCompanyChange,
  className,
  selectComponent: SelectComponent, // Accept custom Select component
  style,
}) => {
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user companies from API
  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = Cookies.get('access_token');
      const userId = Cookies.get('userId');

      if (!accessToken || !userId) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${apiBaseUrl}/users/companies`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          Userid: userId,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }

      const data = await response.json();
      setCompanies(data.companies || []);
      setActiveCompanyId(data.activeCompanyId || null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  // Switch active company
  const switchCompany = async (companyId) => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = Cookies.get('access_token');
      const userId = Cookies.get('userId');

      if (!accessToken || !userId) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${apiBaseUrl}/users/active-company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          Userid: userId,
        },
        body: JSON.stringify({ company_id: companyId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to switch company');
      }

      setActiveCompanyId(companyId);

      // Update cookie for UI state (not authoritative)
      if (domainValue) {
        Cookies.set('company_id', companyId, { domain: domainValue });
      }

      // Notify parent component
      if (onCompanyChange) {
        onCompanyChange(companyId);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error switching company:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  if (companies.length === 0 && !loading) {
    return null;
  }

  if (companies.length === 1) {
    return null; // Don't show selector if user has only one company
  }

  const options = companies.map((company) => ({
    value: company.id,
    label: company.name,
  }));

  // Default native select if no custom component provided
  if (!SelectComponent) {
    return (
      <div className={className} style={style}>
        <select
          value={activeCompanyId || ''}
          onChange={(e) => switchCompany(Number(e.target.value))}
          disabled={loading}
          style={{
            minWidth: 200,
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
          }}
        >
          <option value="" disabled>
            Select company
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && (
          <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>{error}</div>
        )}
      </div>
    );
  }

  // Custom Select component (tt-ui-lib, eco-ui-lib, antd, etc.)
  return (
    <div className={className} style={style}>
      <SelectComponent
        value={activeCompanyId}
        options={options}
        onChange={switchCompany}
        loading={loading}
        placeholder="Select company"
        disabled={loading}
        style={{ minWidth: 200 }}
      />
      {error && <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>{error}</div>}
    </div>
  );
};
